import React from 'react';
import { HeroSection } from '../components/HeroSection';
import { Button } from '../components/ui/Button';
import { motion } from 'framer-motion';
import { BookOpen, Users, Video } from 'lucide-react';
export function LearningGrowth() {
  const courses = [{
    title: 'Unlearning Anti-Fat Bias 101',
    description: 'A 6-week foundational course exploring the roots of fatphobia and how it shows up in our daily lives.',
    status: 'Enrolling Now',
    type: 'Cohort Based'
  }, {
    title: 'History of Fat Activism',
    description: 'Trace the lineage of the movement from the 1960s to today. Understand the shoulders we stand on.',
    status: 'Self-Paced',
    type: 'Video Series'
  }, {
    title: 'Medical Advocacy for Fat Folks',
    description: 'Practical tools and scripts for navigating the healthcare system while fat. Led by fat-friendly providers.',
    status: 'Coming Soon',
    type: 'Workshop'
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-2232">
      <HeroSection title="Learning & Growth" subtitle="We don't just publish ideas — we facilitate deep unlearning and community growth." tag="Education" bgColor="cyan" textColor="navy" data-id="element-2233" />

      {/* Courses Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-2234">
        <div className="flex justify-between items-end mb-12" data-id="element-2235">
          <div data-id="element-2236">
            <h2 className="text-4xl font-black uppercase mb-4 text-brand-black" data-id="element-2237">
              Current Offerings
            </h2>
            <p className="text-gray-600" data-id="element-2238">
              Justice-led education, not compliance-driven training.
            </p>
          </div>
          <Button variant="outline" color="cyan" data-id="element-2239">
            View All Courses
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2240">
          {courses.map((course, index) => <motion.div key={index} whileHover={{
          y: -5
        }} className="bg-white border-2 border-brand-black p-0 flex flex-col h-full" data-id="element-2241">
              <div className="h-48 bg-gray-100 border-b-2 border-brand-black relative overflow-hidden" data-id="element-2242">
                <div className="absolute inset-0 bg-brand-cyan/20" data-id="element-2243"></div>
                <div className="absolute top-4 right-4 bg-white px-3 py-1 text-xs font-bold uppercase border border-brand-black" data-id="element-2244">
                  {course.type}
                </div>
              </div>
              <div className="p-6 flex flex-col flex-grow" data-id="element-2245">
                <h3 className="text-xl font-black uppercase mb-3 text-brand-black" data-id="element-2246">
                  {course.title}
                </h3>
                <p className="text-gray-600 text-sm mb-6 flex-grow" data-id="element-2247">
                  {course.description}
                </p>
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100" data-id="element-2248">
                  <span className="text-sm font-bold text-brand-cyan uppercase" data-id="element-2249">
                    {course.status}
                  </span>
                  <Button size="sm" variant="ghost" color="cyan" withArrow data-id="element-2250">
                    Details
                  </Button>
                </div>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Ways to Learn */}
      <section className="py-20 bg-white border-y border-gray-100" data-id="element-2251">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2252">
          <h2 className="text-4xl font-black uppercase mb-16 text-center text-brand-black" data-id="element-2253">
            Ways to Learn With Us
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-2254">
            <div className="flex flex-col items-center" data-id="element-2255">
              <div className="w-20 h-20 bg-brand-cyan/10 rounded-full flex items-center justify-center mb-6 text-brand-cyan" data-id="element-2256">
                <Video className="h-10 w-10" data-id="element-2257" />
              </div>
              <h3 className="text-xl font-black uppercase mb-3" data-id="element-2258">Self-Paced</h3>
              <p className="text-gray-600" data-id="element-2259">
                Access our library of recorded workshops and guides on your own
                schedule.
              </p>
            </div>

            <div className="flex flex-col items-center" data-id="element-2260">
              <div className="w-20 h-20 bg-brand-cyan/10 rounded-full flex items-center justify-center mb-6 text-brand-cyan" data-id="element-2261">
                <Users className="h-10 w-10" data-id="element-2262" />
              </div>
              <h3 className="text-xl font-black uppercase mb-3" data-id="element-2263">
                Cohort-Based
              </h3>
              <p className="text-gray-600" data-id="element-2264">
                Join a group of peers for deep dives, discussion, and collective
                processing.
              </p>
            </div>

            <div className="flex flex-col items-center" data-id="element-2265">
              <div className="w-20 h-20 bg-brand-cyan/10 rounded-full flex items-center justify-center mb-6 text-brand-cyan" data-id="element-2266">
                <BookOpen className="h-10 w-10" data-id="element-2267" />
              </div>
              <h3 className="text-xl font-black uppercase mb-3" data-id="element-2268">
                Reading Groups
              </h3>
              <p className="text-gray-600" data-id="element-2269">
                Facilitated discussions of key texts in fat liberation and
                disability justice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Individuals vs Collectives */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2270">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-2 border-brand-black" data-id="element-2271">
          <div className="p-12 bg-white border-b-2 md:border-b-0 md:border-r-2 border-brand-black" data-id="element-2272">
            <h3 className="text-3xl font-black uppercase mb-6 text-brand-cyan" data-id="element-2273">
              For Individuals
            </h3>
            <p className="text-gray-700 mb-8 text-lg" data-id="element-2274">
              Looking to deepen your own political analysis? We offer sliding
              scale pricing and community scholarships.
            </p>
            <Button variant="outline" color="cyan" data-id="element-2275">
              Browse Individual Plans
            </Button>
          </div>

          <div className="p-12 bg-brand-navy text-white" data-id="element-2276">
            <h3 className="text-3xl font-black uppercase mb-6 text-brand-lime" data-id="element-2277">
              For Collectives
            </h3>
            <p className="text-gray-300 mb-8 text-lg" data-id="element-2278">
              Want to bring this work to your organization or community group?
              We offer facilitated training and consulting.
            </p>
            <Button variant="primary" color="lime" data-id="element-2279">
              Work With Us
            </Button>
          </div>
        </div>
      </section>
    </div>;
}